import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetDashboardDataQuery } from 'store/services/dashboard.service';
import { useExcludeDisabled } from '../disabledFilter';

// The one place a period token becomes a pair of dates. The topbar chips in
// index.js and the request body below both resolve through here, so a new
// quick-pick option only has to be added once.
export const PERIOD_TO_RANGE = {
    'This month': () => monthRange(0),
    'Last month': () => monthRange(1),
    'Today': () => {
        const today = new Date().toISOString().slice(0, 10);
        return { start: today, end: today };
    },
    '7d':  () => offsetRange(7),
    '30d': () => offsetRange(30),
    '90d': () => offsetRange(90),
    '6m':  () => offsetRange(180),
    '12m': () => offsetRange(365),
};

function offsetRange(days) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

const iso = (d) => d.toISOString().slice(0, 10);

// Calendar-month windows, unlike the trailing windows above. Date.UTC
// normalises an out-of-range month, so January rolls back to December of the
// previous year, and day 0 of month m + 1 is the last day of month m. This
// month runs to today; last month ends on its own final day, which makes it
// the only option whose window does not end now.
function monthRange(monthsBack) {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth() - monthsBack;
    return {
        start: iso(new Date(Date.UTC(y, m, 1))),
        end: monthsBack === 0 ? iso(now) : iso(new Date(Date.UTC(y, m + 1, 0))),
    };
}

export function resolveDateRange({ startDate, endDate, period }) {
    if (startDate && endDate) return { start: startDate, end: endDate };
    const fn = PERIOD_TO_RANGE[period] || PERIOD_TO_RANGE['90d'];
    return fn();
}

// The same window as epoch milliseconds, for pages that filter a local event
// log rather than issuing a query.
// ponytail: SimsPage, CoursesPage, SendHistoryPage and CourseSendHistoryPage
// still carry their own near-identical copies of this; fold them in here if a
// third behaviour difference shows up between them.
export function periodWindowMs({ period, startDate, endDate }) {
    const { start, end } = resolveDateRange({ period, startDate, endDate });
    return [Date.parse(`${start}T00:00:00Z`), Date.parse(`${end}T23:59:59Z`)];
}

// Rows of a local send log whose send date falls in the window the server is
// asked for, so client-side fallbacks match the server figures (BD-1003).
export function logInPeriod(log, { period, startDate, endDate }) {
    const [ws, we] = periodWindowMs({ period, startDate, endDate });
    return (log || []).filter((e) => {
        const ts = Date.parse(`${e.dateSent || e.sentAt || ''}T12:00:00Z`);
        return Number.isFinite(ts) && ts >= ws && ts <= we;
    });
}

// ownScope: the caller builds its own population filter (the export builder) and
// already carries excludeDisabled in request.scope, so the dashboard toggle is not
// applied on top of it.
export default function useDashboardData(request, { skip = false, ownScope = false } = {}) {
    const tenancyId = useSelector((s) => s.tenancy.tenancyId);
    const excludeDisabled = useExcludeDisabled() && !ownScope;

    const body = useMemo(() => {
        if (!request) return null;
        const { period, startDate, endDate, ...rest } = request;
        return {
            tenancyId: Number(tenancyId),
            ...rest,
            ...(excludeDisabled ? { scope: { ...(rest.scope || {}), excludeDisabled: true } } : {}),
            filters: {
                ...(rest.filters || {}),
                dateRange: resolveDateRange({ startDate, endDate, period }),
            },
        };
    }, [request, tenancyId, excludeDisabled]);

    return useGetDashboardDataQuery(body, { skip: skip || !tenancyId || !body });
}
